import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, Edit3, Eye, Package } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import MetaTags from '../components/MetaTags';
import ModelViewer from '../components/ModelViewer';
import { CLOSET } from '../data/closet';
import { normalizePiece } from '../utils/pieceNormalizer';
import VerifiedBadge from '../components/VerifiedBadge';

/**
 * OutfitPage - Dedicated shareable page for individual outfits
 * Accessible via /outfit/:outfitId
 * Provides rich social media previews with Open Graph meta tags
 */
const OutfitPage = () => {
  const { outfitId } = useParams();
  const navigate = useNavigate();
  const { publicKey, isConnected } = useWallet();
  const { solPrice } = useSolPrice();
  const [outfit, setOutfit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [shareNotification, setShareNotification] = useState(false);

  // Load outfit from localStorage
  useEffect(() => {
    const loadOutfit = () => {
      try {
        const outfitsData = localStorage.getItem('outfits');
        if (!outfitsData) {
          setLoading(false);
          return;
        }

        const outfits = JSON.parse(outfitsData);
        const foundOutfit = outfits.find(o => o.id === outfitId);
        
        if (foundOutfit) {
          setOutfit(foundOutfit);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading outfit:', error);
        setLoading(false);
      }
    };

    loadOutfit();
  }, [outfitId]);

  // Helper function to get model price
  const getModelPrice = (model) => {
    if (model.price && parseFloat(model.price) > 0) {
      return parseFloat(model.price);
    }
    
    if (model.id) {
      const closetItem = CLOSET.find(item => item.id === model.id);
      if (closetItem && closetItem.price) {
        return parseFloat(closetItem.price);
      }
    }
    
    const closetItem = CLOSET.find(item => 
      item.name === model.name && item.category === model.category
    );
    if (closetItem && closetItem.price) {
      return parseFloat(closetItem.price);
    }
    
    return 0;
  };

  // Calculate total outfit value
  const totalPrice = outfit && Array.isArray(outfit.models) 
    ? outfit.models.reduce((sum, model) => sum + getModelPrice(model), 0) 
    : 0;

  // Handle share
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: outfit.name,
      text: outfit.description || `Check out this amazing outfit: ${outfit.name}`,
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareNotification(true);
        setTimeout(() => setShareNotification(false), 3000);
      }
    } catch (error) {
      console.log('Share failed:', error);
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareNotification(true);
        setTimeout(() => setShareNotification(false), 3000);
      } catch (clipboardError) {
        console.error('Clipboard failed:', clipboardError);
      }
    }
  };

  // Handle edit (only if user owns the outfit)
  const handleEdit = () => {
    navigate(`/outfit-builder?edit=${outfitId}`);
  };

  // Handle toggle favorite
  const handleToggleFavorite = () => {
    if (!outfit) return;

    try {
      const outfitsData = localStorage.getItem('outfits');
      if (!outfitsData) return;

      const outfits = JSON.parse(outfitsData);
      const updatedOutfits = outfits.map(o => 
        o.id === outfitId 
          ? { ...o, isFavorite: !o.isFavorite }
          : o
      );
      
      localStorage.setItem('outfits', JSON.stringify(updatedOutfits));
      setOutfit({ ...outfit, isFavorite: !outfit.isFavorite });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading outfit...</p>
        </div>
      </div>
    );
  }

  if (!outfit) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">👕</div>
          <h1 className="text-2xl font-bold text-white mb-2">Outfit Not Found</h1>
          <p className="text-gray-400 mb-6">This outfit doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/outfits')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-semibold"
          >
            Browse Outfits
          </button>
        </div>
      </div>
    );
  }

  // Generate image URL for meta tags (use first piece thumbnail or placeholder)
  const metaImageUrl = outfit.image || 
    (outfit.models && outfit.models[0]?.thumbnailUrl) || 
    (outfit.models && outfit.models[0]?.thumbnail?.dataUrl) ||
    '/images/piece-placeholder.png';

  const fullImageUrl = metaImageUrl.startsWith('http') 
    ? metaImageUrl 
    : `${window.location.origin}${metaImageUrl}`;

  return (
    <>
      {/* Meta Tags for Social Sharing */}
      <MetaTags
        title={`${outfit.name} - Outsera Outfit`}
        description={outfit.description || `A ${outfit.models?.length || 0}-piece outfit curated on Outsera. Total value: ${totalPrice.toFixed(2)} SOL`}
        image={fullImageUrl}
        url={window.location.href}
        type="website"
      />

      <div className="min-h-screen bg-black">
        {/* Share Notification */}
        {shareNotification && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
            <Share2 size={18} />
            <span>Link copied to clipboard!</span>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/outfits')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span>Back to Outfits</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Outfit Preview */}
            <div>
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 sticky top-4">
                {/* Outfit Image/Preview */}
                <div className="aspect-square bg-[#1a1a1a] rounded-xl mb-6 flex items-center justify-center overflow-hidden">
                  {outfit.image ? (
                    <img 
                      src={outfit.image} 
                      alt={outfit.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-500 text-center">
                      <Eye size={64} className="mx-auto mb-2" />
                      <p>No preview image</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg"
                  >
                    <Share2 size={18} />
                    Share Outfit
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={handleToggleFavorite}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                        outfit.isFavorite 
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                          : 'bg-[#2a2a2a] text-gray-400 hover:bg-[#3a3a3a] hover:text-red-400 border border-[#2a2a2a]'
                      }`}
                    >
                      <Heart size={18} className={outfit.isFavorite ? 'fill-current' : ''} />
                      {outfit.isFavorite ? 'Favorited' : 'Favorite'}
                    </button>

                    {isConnected && (
                      <button
                        onClick={handleEdit}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-400 hover:text-white rounded-xl transition-all duration-200 font-medium border border-[#2a2a2a]"
                      >
                        <Edit3 size={18} />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Outfit Details */}
            <div>
              {/* Outfit Info */}
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 mb-6">
                <h1 className="text-3xl font-bold text-white mb-3">{outfit.name}</h1>
                
                {/* Tags */}
                {outfit.tags && outfit.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {outfit.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#1a1a1a] text-gray-300 text-sm rounded-full border border-[#2a2a2a]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-2">Description</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {outfit.description || 'A carefully curated outfit combination with premium digital fashion pieces.'}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Total Value</div>
                    <div className="text-green-400 font-bold text-xl">
                      {totalPrice.toFixed(2)} SOL
                    </div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(totalPrice * solPrice).toFixed(2)} USD
                    </div>
                  </div>

                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Pieces</div>
                    <div className="text-white font-bold text-xl">
                      {outfit.models?.length || 0}
                    </div>
                    <div className="text-gray-500 text-sm">
                      {outfit.models?.length === 1 ? 'Item' : 'Items'}
                    </div>
                  </div>
                </div>

                {/* Created Date */}
                {outfit.createdAt && (
                  <div className="text-gray-500 text-sm">
                    Created {new Date(outfit.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                )}
              </div>

              {/* Outfit Pieces */}
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold text-lg">Outfit Pieces</h3>
                  <span className="text-gray-400 text-sm bg-[#1a1a1a] px-3 py-1 rounded-full border border-[#2a2a2a]">
                    {outfit.models?.length || 0} {outfit.models?.length === 1 ? 'piece' : 'pieces'}
                  </span>
                </div>

                {outfit.models && outfit.models.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {outfit.models.map((model, index) => (
                      <div 
                        key={index}
                        className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-3 hover:border-white/50 transition-all duration-200 cursor-pointer"
                        onClick={() => setSelectedPiece(model)}
                      >
                        {/* Piece Thumbnail */}
                        <div className="aspect-square bg-[#2a2a2a] rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                          {model.thumbnail?.dataUrl || model.thumbnailUrl || model.image ? (
                            <img 
                              src={model.thumbnail?.dataUrl || model.thumbnailUrl || model.image} 
                              alt={model.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package size={32} className="text-gray-500" />
                          )}
                        </div>
                        
                        {/* Piece Name */}
                        <h4 className="text-white font-medium text-sm truncate mb-1">
                          {model.name}
                        </h4>
                        
                        {/* Piece Price */}
                        <div className="text-green-400 text-xs font-semibold">
                          {getModelPrice(model).toFixed(2)} SOL
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">👕</div>
                    <div className="text-gray-400 text-sm">No pieces in this outfit</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OutfitPage;

